/** Permanent trail points in canvas pixel space. */
const trailPoints: { x: number; y: number }[] = [];

export const SNAKE_APPLE_START_COUNT = 8;
export const SNAKE_APPLE_SPEED = 0.8;

/** @deprecated Singleplayer no longer spawns on score — kept for reference only. */
export const SNAKE_SCORE_PER_SPAWN = 500;
/** @deprecated Singleplayer no longer spawns on score — kept for reference only. */
export const SNAKE_BALLS_PER_SPAWN = 55;

type SnakeAppleLike = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    r: number;
    lastBounceFx: number;
    angle?: number;
};

const SNAKE_APPLE_SPIN_RATE = 0.048;

export function resetSnakeTrail(): void {
    trailPoints.length = 0;
}

export function seedSnakeTrail(x: number, y: number): void {
    trailPoints.length = 0;
    trailPoints.push({ x, y });
}

function trailHalfWidth(layoutScale: number): number {
    return Math.max(4 * layoutScale, 5);
}

function minPointSpacing(layoutScale: number): number {
    return Math.max(2.5 * layoutScale, 3.5);
}

export function appendSnakeTrailPoint(x: number, y: number, layoutScale: number): void {
    const spacing = minPointSpacing(layoutScale);
    if (trailPoints.length === 0) {
        trailPoints.push({ x, y });
        return;
    }
    const last = trailPoints[trailPoints.length - 1]!;
    const dx = x - last.x;
    const dy = y - last.y;
    const dist = Math.hypot(dx, dy);
    if (dist < spacing * 0.25) return;
    const steps = Math.max(1, Math.ceil(dist / spacing));
    for (let s = 1; s <= steps; s++) {
        const t = s / steps;
        trailPoints.push({ x: last.x + dx * t, y: last.y + dy * t });
    }
}

function distPointToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-12) return Math.hypot(px - x1, py - y1);
    let u = ((px - x1) * dx + (py - y1) * dy) / len2;
    u = Math.max(0, Math.min(1, u));
    const qx = x1 + u * dx;
    const qy = y1 + u * dy;
    return Math.hypot(px - qx, py - qy);
}

function distSegmentToSegment(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number,
    dx: number,
    dy: number,
): number {
    const ux = bx - ax;
    const uy = by - ay;
    const vx = dx - cx;
    const vy = dy - cy;
    const uLen2 = ux * ux + uy * uy;
    const vLen2 = vx * vx + vy * vy;
    if (uLen2 < 1e-12 && vLen2 < 1e-12) return Math.hypot(ax - cx, ay - cy);
    if (uLen2 < 1e-12) return distPointToSegment(ax, ay, cx, cy, dx, dy);
    if (vLen2 < 1e-12) return distPointToSegment(cx, cy, ax, ay, bx, by);

    let t = ((cx - ax) * ux + (cy - ay) * uy) / uLen2;
    let s = ((ax - cx) * vx + (ay - cy) * vy) / vLen2;
    t = Math.max(0, Math.min(1, t));
    s = Math.max(0, Math.min(1, s));
    const px = ax + t * ux;
    const py = ay + t * uy;
    const qx = cx + s * vx;
    const qy = cy + s * vy;
    return Math.hypot(px - qx, py - qy);
}

function firstCollidableSegmentIndex(gracePx: number): number {
    if (trailPoints.length < 2) return 0;
    let graceLeft = gracePx;
    let firstCheckIndex = trailPoints.length - 2;
    while (firstCheckIndex > 0 && graceLeft > 0) {
        const a = trailPoints[firstCheckIndex - 1]!;
        const b = trailPoints[firstCheckIndex]!;
        graceLeft -= Math.hypot(b.x - a.x, b.y - a.y);
        firstCheckIndex--;
    }
    return firstCheckIndex;
}

export function snakeSelfCollision(
    cx: number,
    cy: number,
    hitRadius: number,
    layoutScale: number,
    prevX?: number,
    prevY?: number,
): boolean {
    if (trailPoints.length < 4) return false;
    const threshold = hitRadius + trailHalfWidth(layoutScale);
    let gracePx = Math.max(48 * layoutScale, hitRadius * 7);
    gracePx += threshold;
    if (
        prevX != null &&
        prevY != null &&
        Number.isFinite(prevX) &&
        Number.isFinite(prevY)
    ) {
        gracePx += Math.hypot(cx - prevX, cy - prevY);
    }
    const firstCheckIndex = firstCollidableSegmentIndex(gracePx);

    for (let i = 0; i < firstCheckIndex; i++) {
        const a = trailPoints[i]!;
        const b = trailPoints[i + 1]!;
        if (distPointToSegment(cx, cy, a.x, a.y, b.x, b.y) < threshold) return true;
        if (
            prevX != null &&
            prevY != null &&
            Number.isFinite(prevX) &&
            Number.isFinite(prevY) &&
            distSegmentToSegment(prevX, prevY, cx, cy, a.x, a.y, b.x, b.y) < threshold
        ) {
            return true;
        }
    }
    return false;
}

export function snakeBorderCollision(
    cx: number,
    cy: number,
    hitRadius: number,
    w: number,
    h: number,
    inset: number,
): boolean {
    if (w <= 0 || h <= 0) return false;
    const bounds = snakePlayBounds(w, h, inset);
    return (
        cx - hitRadius < bounds.minX ||
        cx + hitRadius > bounds.maxX ||
        cy - hitRadius < bounds.minY ||
        cy + hitRadius > bounds.maxY
    );
}

export function snakePlayBounds(w: number, h: number, inset: number): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
} {
    const minX = w >= 2 * inset ? inset : w * 0.5;
    const maxX = w >= 2 * inset ? w - inset : w * 0.5;
    const minY = h >= 2 * inset ? inset : h * 0.5;
    const maxY = h >= 2 * inset ? h - inset : h * 0.5;
    return { minX, maxX, minY, maxY };
}

/** Visible play-area border (Snake only). Touching it kills the player. */
export function drawSnakePlayBorder(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    inset: number,
    color: string,
    layoutScale: number,
): void {
    if (w <= 0 || h <= 0) return;
    const { minX, maxX, minY, maxY } = snakePlayBounds(w, h, inset);
    const width = maxX - minX;
    const height = maxY - minY;
    if (width <= 0 || height <= 0) return;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, 2 * layoutScale);
    ctx.shadowColor = color;
    ctx.shadowBlur = 8 * layoutScale;
    ctx.globalAlpha = 0.95;
    ctx.strokeRect(minX, minY, width, height);
    ctx.restore();
}

export function drawSnakeTrail(ctx: CanvasRenderingContext2D, layoutScale: number, color: string): void {
    if (trailPoints.length < 2) return;
    const half = trailHalfWidth(layoutScale);
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = color;
    ctx.lineWidth = half * 2;
    ctx.shadowColor = color;
    ctx.shadowBlur = 10 * layoutScale;
    ctx.globalAlpha = 0.92;
    ctx.beginPath();
    ctx.moveTo(trailPoints[0]!.x, trailPoints[0]!.y);
    for (let i = 1; i < trailPoints.length; i++) {
        ctx.lineTo(trailPoints[i]!.x, trailPoints[i]!.y);
    }
    ctx.stroke();
    ctx.restore();
}

export function updateSnakeApple(
    apple: SnakeAppleLike,
    mult: number,
    w: number,
    h: number,
    inset: number,
): boolean {
    if (apple.angle == null) apple.angle = Math.random() * Math.PI * 2;
    apple.angle += SNAKE_APPLE_SPIN_RATE * mult;

    apple.x += apple.vx * mult;
    apple.y += apple.vy * mult;

    const { minX, maxX, minY, maxY } = snakePlayBounds(w, h, inset);
    let bounced = false;

    if (apple.x < minX + apple.r) {
        apple.x = minX + apple.r;
        apple.vx *= -1;
        bounced = true;
    } else if (apple.x > maxX - apple.r) {
        apple.x = maxX - apple.r;
        apple.vx *= -1;
        bounced = true;
    }

    if (apple.y < minY + apple.r) {
        apple.y = minY + apple.r;
        apple.vy *= -1;
        bounced = true;
    } else if (apple.y > maxY - apple.r) {
        apple.y = maxY - apple.r;
        apple.vy *= -1;
        bounced = true;
    }

    if (bounced) {
        const boost = 1.08;
        apple.vx *= boost;
        apple.vy *= boost;
    }
    return bounced;
}

/** Bounce a moving apple off the permanent snake trail. */
export function resolveSnakeAppleTrailCollision(apple: SnakeAppleLike, layoutScale: number): boolean {
    if (trailPoints.length < 2) return false;
    const threshold = apple.r + trailHalfWidth(layoutScale);
    let collided = false;

    for (let i = 0; i < trailPoints.length - 1; i++) {
        const a = trailPoints[i]!;
        const b = trailPoints[i + 1]!;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len2 = dx * dx + dy * dy;
        let qx: number;
        let qy: number;
        if (len2 < 1e-12) {
            qx = a.x;
            qy = a.y;
        } else {
            let u = ((apple.x - a.x) * dx + (apple.y - a.y) * dy) / len2;
            u = Math.max(0, Math.min(1, u));
            qx = a.x + u * dx;
            qy = a.y + u * dy;
        }
        const distX = apple.x - qx;
        const distY = apple.y - qy;
        const dist = Math.hypot(distX, distY);
        if (dist >= threshold || dist < 1e-8) continue;

        const nx = distX / dist;
        const ny = distY / dist;
        const penetration = threshold - dist;
        apple.x += nx * penetration;
        apple.y += ny * penetration;

        const vDot = apple.vx * nx + apple.vy * ny;
        if (vDot < 0) {
            apple.vx -= 2 * vDot * nx;
            apple.vy -= 2 * vDot * ny;
        }
        collided = true;
    }
    return collided;
}

export function drawSnakeApple(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
    layoutScale: number,
    angle = 0,
): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    ctx.fillStyle = "#e53e3e";
    ctx.shadowColor = "#ff6b6b";
    ctx.shadowBlur = Math.max(4, r * 0.55);
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.fillStyle = "rgba(255,255,255,0.28)";
    ctx.beginPath();
    ctx.arc(-r * 0.28, -r * 0.28, r * 0.34, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = "#6b4423";
    ctx.lineWidth = Math.max(1.5, 2 * layoutScale);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.lineTo(r * 0.18, -r * 1.38);
    ctx.stroke();

    ctx.fillStyle = "#38a169";
    ctx.beginPath();
    ctx.ellipse(r * 0.38, -r * 1.05, r * 0.36, r * 0.2, 0.45, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}
